/**
 * Per-control autosave for the user settings page (app/views/users/settings.html.erb).
 *
 * The page used to have a single Save button at the very bottom of a long form.
 * Now every control saves itself: changes are turned into a partial
 * `{ user: { … } }` payload, coalesced over a short debounce window and PATCHed
 * to `/user`. One row UPDATE per settled change is cheap — the debounce is what
 * keeps a user dragging through a select from generating a request per keystroke.
 *
 * The form still submits normally without JavaScript: `initSettingsAutosave`
 * only hides the fallback Save button once it has successfully wired itself up.
 *
 * The pure helpers below (payload shaping, preset matching) hold all the logic
 * worth testing; see __tests__/settingsAutosave.test.ts.
 */

import { i18n } from '../i18n';

const t = (key: string): string => i18n.global.t(key);

export type SettingsPayload = Record<string, string>;

export type SettingsControlSnapshot =
  | { kind: 'value'; attribute: string; value: string }
  | {
      kind: 'checkbox';
      attribute: string;
      checked: boolean;
      checkedValue: string;
      uncheckedValue: string;
    };

export interface HikeTimePreset {
  key: string;
  distance: string;
  elevation: string;
}

const FIELD_NAME_PATTERN = /^user\[([a-z0-9_]+)\]$/;

/** `user[over_zoom]` -> `over_zoom`. Anything else -> null. */
export function userAttributeFromFieldName(name: string | null | undefined): string | null {
  if (!name) return null;
  const match = FIELD_NAME_PATTERN.exec(name.trim());
  return match ? match[1] : null;
}

/**
 * Shapes one control's current state into the attributes to send.
 *
 * Checkboxes carry their own value pair rather than assuming 1/0: `is_public`
 * on this page is rendered inverted (ticking "Make my account private" submits
 * `is_public=false`), so the checked/unchecked values have to come from the DOM
 * instead of being inferred here.
 */
export function controlPayload(snapshot: SettingsControlSnapshot): SettingsPayload | null {
  if (!snapshot.attribute) return null;

  if (snapshot.kind === 'checkbox') {
    return {
      [snapshot.attribute]: snapshot.checked ? snapshot.checkedValue : snapshot.uncheckedValue,
    };
  }

  return { [snapshot.attribute]: snapshot.value };
}

/** Later changes to the same attribute win, so a flush sends one value per field. */
export function mergePayloads(pending: SettingsPayload, next: SettingsPayload): SettingsPayload {
  return { ...pending, ...next };
}

/**
 * Attributes whose submitted value the server may legitimately ignore, so the
 * UI must not claim they were stored. `measurement_preference` has a blank
 * "Not set" option that the controller deliberately drops rather than
 * fabricating a units choice the user never made.
 */
export function isNoopChange(attribute: string, value: string): boolean {
  return attribute === 'measurement_preference' && value.trim() === '';
}

export function matchesHikeTimePreset(
  preset: HikeTimePreset,
  distance: string,
  elevation: string
): boolean {
  return preset.distance === distance && preset.elevation === elevation;
}

export function activeHikeTimePreset(
  presets: HikeTimePreset[],
  distance: string,
  elevation: string
): HikeTimePreset | null {
  return presets.find((preset) => matchesHikeTimePreset(preset, distance, elevation)) ?? null;
}

/**
 * Which pane to open when the user has no stored choice: the presets, unless
 * their saved pace is a custom combination no preset can express.
 */
export function defaultHikeTimeMode(
  presets: HikeTimePreset[],
  distance: string,
  elevation: string
): 'basic' | 'advanced' {
  return activeHikeTimePreset(presets, distance, elevation) ? 'basic' : 'advanced';
}

export function resolveHikeTimeMode(
  storedMode: string | null,
  presets: HikeTimePreset[],
  distance: string,
  elevation: string
): 'basic' | 'advanced' {
  if (storedMode === 'basic' || storedMode === 'advanced') return storedMode;
  return defaultHikeTimeMode(presets, distance, elevation);
}

/** One user-facing sentence for a failed save. */
export function describeSaveFailure(status: number, body: unknown): string {
  const errors = (body as { errors?: unknown } | null)?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.filter((error) => typeof error === 'string' && error.length > 0).join(', ');
  }
  if (status === 401 || status === 403) {
    return t('utils.settings_autosave.sign_in_again');
  }
  if (status === 0) {
    return t('utils.settings_autosave.could_not_reach_hiiker');
  }
  return t('utils.settings_autosave.could_not_save_try_again');
}

/* ------------------------------------------------------------------ */
/* DOM wiring                                                          */
/* ------------------------------------------------------------------ */

const SELECT_DEBOUNCE_MS = 250;
const TEXT_DEBOUNCE_MS = 900;

type StatusTone = 'idle' | 'saving' | 'saved' | 'error';

function isFormControl(
  element: Element
): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  );
}

function snapshotFor(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  form: HTMLFormElement
): SettingsControlSnapshot | null {
  const attribute = userAttributeFromFieldName(element.getAttribute('name'));
  if (!attribute) return null;

  if (element instanceof HTMLInputElement && element.type === 'checkbox') {
    // Rails renders a hidden companion field carrying the unchecked value.
    const hidden = form.querySelector<HTMLInputElement>(
      `input[type="hidden"][name="${CSS.escape(element.name)}"]`
    );
    return {
      kind: 'checkbox',
      attribute,
      checked: element.checked,
      checkedValue: element.value || '1',
      uncheckedValue: hidden ? hidden.value : '0',
    };
  }

  return { kind: 'value', attribute, value: element.value };
}

export function initSettingsAutosave(root: ParentNode = document): void {
  const container = root.querySelector<HTMLElement>('[data-settings-autosave]');
  if (!container) return;

  const form = container.querySelector<HTMLFormElement>('form');
  const status = container.querySelector<HTMLElement>('[data-settings-save-status]');
  const fallback = container.querySelector<HTMLElement>('[data-settings-fallback-save]');
  const url = container.dataset.settingsUrl || form?.getAttribute('action');
  if (!form || !url) return;

  let pending: SettingsPayload = {};
  let timer: number | null = null;
  let inFlight = 0;
  let announcedNoop = false;

  const setStatus = (tone: StatusTone, message?: string) => {
    if (!status) return;
    status.className = tone === 'idle' ? 'settings-save-status' : `settings-save-status settings-save-status--${tone}`;
    status.hidden = tone === 'idle';
    status.textContent =
      message ??
      {
        idle: () => '',
        saving: () => t('utils.settings_autosave.saving'),
        saved: () => t('utils.settings_autosave.saved'),
        error: () => t('utils.settings_autosave.could_not_save'),
      }[tone]();
  };

  const csrfToken = () =>
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

  const send = async (body: BodyInit, extraHeaders: Record<string, string> = {}) => {
    inFlight += 1;
    setStatus('saving');
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken(), ...extraHeaders },
        body,
      });
      const json = await response.json().catch(() => null);
      // Demand the server's own `saved: true`. A 2xx alone isn't proof: an
      // expired session redirects to the login page, which fetch follows and
      // hands back as a 200 full of HTML.
      if (!response.ok || (json as { saved?: unknown } | null)?.saved !== true) {
        const status = response.ok ? 401 : response.status;
        setStatus('error', describeSaveFailure(status, json));
        return;
      }
      applyServerState(json);
      // Only the last request settling gets to claim everything is saved.
      if (inFlight === 1) setStatus('saved');
    } catch (error) {
      console.error('Settings autosave failed', error);
      setStatus('error', describeSaveFailure(0, null));
    } finally {
      inFlight -= 1;
    }
  };

  const sendAttributes = (payload: SettingsPayload) =>
    send(JSON.stringify({ user: payload }), { 'Content-Type': 'application/json' });

  const applyServerState = (body: unknown) => {
    const avatarUrl = (body as { avatar_url?: unknown } | null)?.avatar_url;
    const avatar = container.querySelector<HTMLImageElement>('[data-settings-avatar]');
    if (avatar && typeof avatarUrl === 'string' && avatarUrl.length > 0) {
      avatar.src = avatarUrl;
    }
  };

  const flush = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    const payload = pending;
    pending = {};
    if (Object.keys(payload).length === 0) return;
    void sendAttributes(payload);
  };

  const queue = (payload: SettingsPayload, delay: number) => {
    pending = mergePayloads(pending, payload);
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(flush, delay);
  };

  const handle = (element: Element, delay: number) => {
    if (!isFormControl(element)) return;
    const snapshot = snapshotFor(element, form);
    if (!snapshot) return;
    const payload = controlPayload(snapshot);
    if (!payload) return;

    const [attribute, value] = Object.entries(payload)[0];
    if (isNoopChange(attribute, value)) {
      // Nothing is stored for a blank units choice, so don't promise it was.
      if (!announcedNoop) {
        announcedNoop = true;
        setStatus('idle');
      }
      return;
    }
    announcedNoop = false;

    queue(payload, delay);
  };

  // A new avatar is not a keystroke — upload it as soon as it is picked, and
  // let the server hand back the processed URL for the thumbnail.
  const sendAvatar = (input: HTMLInputElement) => {
    if (!input.files || input.files.length === 0) return;
    const data = new FormData();
    data.append('user[avatar]', input.files[0]);
    return send(data);
  };

  form.addEventListener('change', (event) => {
    const target = event.target as Element | null;
    if (!target) return;
    if (target instanceof HTMLInputElement && target.type === 'file') {
      void sendAvatar(target);
      return;
    }
    handle(target, SELECT_DEBOUNCE_MS);
  });

  form.addEventListener('input', (event) => {
    const target = event.target as Element | null;
    if (!target || !(target instanceof HTMLInputElement) || target.type !== 'text') return;
    handle(target, TEXT_DEBOUNCE_MS);
  });

  // Leaving a text field is an explicit "I'm done" — don't make the user wait
  // out the debounce, and don't let a navigation drop the edit.
  form.addEventListener('focusout', () => {
    if (Object.keys(pending).length > 0) flush();
  });

  window.addEventListener('beforeunload', (event) => {
    if (Object.keys(pending).length === 0 && inFlight === 0) return;
    flush();
    event.preventDefault();
    // Legacy browsers need a return value to show the confirmation prompt.
    (event as BeforeUnloadEvent).returnValue = '';
  });

  // Autosave is live, so the bottom-of-page Save button is now redundant.
  if (fallback) fallback.hidden = true;
  container.dataset.settingsAutosaveReady = 'true';
  setStatus('idle');
}

export function initHikeTimeSection(root: ParentNode = document): void {
  const section = root.querySelector<HTMLElement>('[data-hike-time-section]');
  if (!section) return;

  const STORAGE_KEY = 'hiiker.settings.hikeTimeMode';
  const basic = section.querySelector<HTMLElement>('[data-hike-time-basic]');
  const advanced = section.querySelector<HTMLElement>('[data-hike-time-advanced]');
  const distanceSelect = section.querySelector<HTMLSelectElement>('select[name="user[hike_time_distance]"]');
  const elevationSelect = section.querySelector<HTMLSelectElement>('select[name="user[hike_time_elevation]"]');
  const buttons = Array.from(section.querySelectorAll<HTMLElement>('[data-hike-time-preset]'));
  const customLabel = section.querySelector<HTMLElement>('[data-hike-time-custom-label]');
  if (!basic || !advanced || !distanceSelect || !elevationSelect) return;

  const presets: HikeTimePreset[] = buttons.map((button) => ({
    key: button.dataset.hikeTimePreset ?? '',
    distance: button.dataset.distance ?? '',
    elevation: button.dataset.elevation ?? '',
  }));

  const refreshChecks = () => {
    const active = activeHikeTimePreset(presets, distanceSelect.value, elevationSelect.value);
    buttons.forEach((button, index) => {
      const isActive = active !== null && presets[index].key === active.key;
      button.classList.toggle('active', isActive);
      const check = button.querySelector<HTMLElement>('[data-hike-time-check]');
      if (check) check.style.display = isActive ? '' : 'none';
    });
    if (customLabel) customLabel.style.display = active ? 'none' : '';
  };

  const applyMode = (mode: 'basic' | 'advanced') => {
    basic.style.display = mode === 'advanced' ? 'none' : '';
    advanced.style.display = mode === 'advanced' ? '' : 'none';
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* Private browsing — the pane choice just doesn't persist. */
    }
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      distanceSelect.value = button.dataset.distance ?? '';
      elevationSelect.value = button.dataset.elevation ?? '';
      distanceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      elevationSelect.dispatchEvent(new Event('change', { bubbles: true }));
      refreshChecks();
    });
  });

  section
    .querySelector('[data-hike-time-show-advanced]')
    ?.addEventListener('click', () => applyMode('advanced'));
  section.querySelector('[data-hike-time-show-basic]')?.addEventListener('click', () => {
    applyMode('basic');
    refreshChecks();
  });

  distanceSelect.addEventListener('change', refreshChecks);
  elevationSelect.addEventListener('change', refreshChecks);

  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  applyMode(resolveHikeTimeMode(stored, presets, distanceSelect.value, elevationSelect.value));
  refreshChecks();
}
